import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, Send } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SmithersOrb() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    { id: 1, from: 'smithers', text: 'Good evening. SMITHERS online and operational. How may I assist you?' }
  ]);
  const [input, setInput] = useState('');
  const [isThinking, setIsThinking] = useState(false);
  const [neonSignOn, setNeonSignOn] = useState(false);
  const neonCanvasRef = useRef(null);
  const orbSvgRef = useRef(null);
  const messagesEndRef = useRef(null);

  // Listen for neon sign toggle from anywhere (Easter egg cord)
  useEffect(() => {
    const handler = (e) => setNeonSignOn(e.detail?.on ?? false);
    window.addEventListener('smithers-neon-toggle', handler);
    return () => window.removeEventListener('smithers-neon-toggle', handler);
  }, []);

  // Process neon sign image (strip checkered background)
  useEffect(() => {
    if (!neonSignOn || !neonCanvasRef.current) return;
    const canvas = neonCanvasRef.current;
    const ctx = canvas.getContext('2d');
    const img = new Image();
    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.drawImage(img, 0, 0);
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const d = imageData.data;
      for (let i = 0; i < d.length; i += 4) {
        const diff = Math.max(d[i], d[i+1], d[i+2]) - Math.min(d[i], d[i+1], d[i+2]);
        if (diff < 40) d[i+3] = 0;
        else if (diff < 60) d[i+3] = Math.floor(d[i+3] * ((diff - 40) / 20));
      }
      ctx.putImageData(imageData, 0, 0);
    };
    img.src = '/images/neon-sign.png';
  }, [neonSignOn]);

  // Auto-scroll messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Build golden spiral SVG animation with pulsing dots
  useEffect(() => {
    const svg = orbSvgRef.current;
    if (!svg) return;

    const SIZE = 64;
    const GOLDEN_ANGLE = Math.PI * (3 - Math.sqrt(5));
    const CENTER = SIZE / 2;
    const totalDots = 250;
    const dotRadius = 0.9;
    const MAX_RADIUS = CENTER - 3;
    const duration = 5;
    // Cyan, Congo Blue, Magenta (dash)
    const colors = ['#00F0FF', '#003B6F', '#FF00E5'];

    while (svg.firstChild) svg.removeChild(svg.firstChild);

    for (let i = 0; i < totalDots; i++) {
      const idx = i + 0.5;
      const frac = idx / totalDots;
      const r = Math.sqrt(frac) * MAX_RADIUS;
      const theta = idx * GOLDEN_ANGLE;
      const x = CENTER + r * Math.cos(theta);
      const y = CENTER + r * Math.sin(theta);

      // Color: cyan core → congo blue middle → magenta edge
      let ci;
      if (frac < 0.5) ci = 0;
      else if (frac < 0.85) ci = 1;
      else ci = 2;

      const circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
      circle.setAttribute('cx', x.toFixed(2));
      circle.setAttribute('cy', y.toFixed(2));
      circle.setAttribute('r', dotRadius.toString());
      circle.setAttribute('fill', colors[ci]);
      circle.setAttribute('opacity', '0');

      // Pulse radius animation
      const animR = document.createElementNS('http://www.w3.org/2000/svg', 'animate');
      animR.setAttribute('attributeName', 'r');
      animR.setAttribute('values', `${dotRadius * 0.5};${dotRadius * 1.3};${dotRadius * 0.5}`);
      animR.setAttribute('dur', `${duration}s`);
      animR.setAttribute('begin', `${(frac * duration).toFixed(2)}s`);
      animR.setAttribute('repeatCount', 'indefinite');
      animR.setAttribute('calcMode', 'spline');
      animR.setAttribute('keySplines', '0.4 0 0.6 1;0.4 0 0.6 1');
      circle.appendChild(animR);

      // Pulse opacity animation
      const animO = document.createElementNS('http://www.w3.org/2000/svg', 'animate');
      animO.setAttribute('attributeName', 'opacity');
      animO.setAttribute('values', '0.15;0.9;0.15');
      animO.setAttribute('dur', `${duration}s`);
      animO.setAttribute('begin', `${(frac * duration).toFixed(2)}s`);
      animO.setAttribute('repeatCount', 'indefinite');
      animO.setAttribute('calcMode', 'spline');
      animO.setAttribute('keySplines', '0.4 0 0.6 1;0.4 0 0.6 1');
      circle.appendChild(animO);

      svg.appendChild(circle);
    }
  }, []);



  const handleSend = useCallback(async () => {
    const trimmed = input.trim();
    if (!trimmed || isThinking) return;

    setMessages(prev => [...prev, { id: Date.now(), from: 'you', text: trimmed }]);
    setInput('');
    setIsThinking(true);

    try {
      const res = await fetch(`${API}/smithers/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: trimmed }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setMessages(prev => [...prev, {
        id: Date.now() + 1,
        from: 'smithers',
        text: data.reply || data.message || data.text || 'Acknowledged.'
      }]);
    } catch {
      setMessages(prev => [...prev, {
        id: Date.now() + 1,
        from: 'smithers',
        text: 'Systems nominal. Backend relay initializing. Stand by.'
      }]);
    } finally {
      setIsThinking(false);
    }
  }, [input, isThinking]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="fixed bottom-6 right-6 z-[9999]" data-testid="smithers-orb-container">
      {/* Chat Panel */}
      {isOpen && (
        <div
          className="absolute bottom-20 right-0 w-[360px] max-w-[calc(100vw-48px)] rounded-2xl overflow-hidden flex flex-col animate-fade-in-up"
          data-testid="smithers-chat-panel"
          style={{
            height: 'min(480px, 70vh)',
            background: 'rgba(10, 10, 12, 0.96)',
            backdropFilter: 'blur(24px)',
            border: '1px solid rgba(0, 240, 255, 0.12)',
            boxShadow: '0 0 40px rgba(0,240,255,0.1), 0 0 80px rgba(0,59,111,0.06), 0 24px 48px rgba(0,0,0,0.6)',
          }}
        >
          {/* Header */}
          <div
            className="flex items-center justify-between px-4 py-3 shrink-0"
            style={{
              borderBottom: '1px solid rgba(0, 240, 255, 0.1)',
              background: 'linear-gradient(180deg, rgba(0,59,111,0.2) 0%, rgba(0,59,111,0.05) 100%)',
            }}
          >
            <div className="flex items-center gap-2.5">
              <div
                className="w-2 h-2 rounded-full"
                style={{ background: '#00F0FF', boxShadow: '0 0 8px #00F0FF, 0 0 16px rgba(0,240,255,0.3)' }}
              />
              <span className="font-brand text-xs font-bold tracking-[0.15em]" style={{ color: '#00F0FF' }}>
                SMITHERS
              </span>
              <span className="text-[9px] tracking-[0.2em] uppercase" style={{ color: '#6B7280' }}>
                AI Presence
              </span>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="w-6 h-6 flex items-center justify-center rounded-full transition-all"
              style={{ color: '#6B7280' }}
              onMouseEnter={(e) => { e.currentTarget.style.color = '#fff'; e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = '#6B7280'; e.currentTarget.style.background = 'transparent'; }}
              data-testid="smithers-close-btn"
            >
              <X size={14} />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-3 space-y-2.5">
            {messages.map(msg => (
              <div key={msg.id} className={msg.from === 'you' ? 'ml-10' : 'mr-6'}>
                <div
                  className="rounded-xl px-3 py-2"
                  style={{
                    background: msg.from === 'you' ? 'rgba(0,240,255,0.06)' : 'rgba(0,59,111,0.15)',
                    border: `1px solid ${msg.from === 'you' ? 'rgba(0,240,255,0.12)' : 'rgba(0,59,111,0.2)'}`,
                  }}
                >
                  <p
                    className="text-[9px] uppercase mb-1 font-label"
                    style={{
                      color: msg.from === 'you' ? '#00F0FF' : '#FF00E5',
                      letterSpacing: '0.2em',
                    }}
                  >
                    {msg.from === 'you' ? 'You' : 'SMITHERS'}
                  </p>
                  <p
                    className="text-[13px] leading-relaxed whitespace-pre-wrap"
                    style={{ color: '#E5E7EB', fontFamily: "'Jost', sans-serif" }}
                  >
                    {msg.text}
                  </p>
                </div>
              </div>
            ))}

            {isThinking && (
              <div className="mr-6">
                <div
                  className="rounded-xl px-3 py-3 inline-block"
                  style={{ background: 'rgba(0,59,111,0.15)', border: '1px solid rgba(0,59,111,0.2)' }}
                >
                  <div className="flex gap-1.5 items-center">
                    <div className="w-1.5 h-1.5 rounded-full animate-bounce" style={{ background: '#00F0FF', animationDelay: '0ms' }} />
                    <div className="w-1.5 h-1.5 rounded-full animate-bounce" style={{ background: '#003B6F', animationDelay: '150ms' }} />
                    <div className="w-1.5 h-1.5 rounded-full animate-bounce" style={{ background: '#FF00E5', animationDelay: '300ms' }} />
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="p-3 shrink-0" style={{ borderTop: '1px solid rgba(0,240,255,0.06)' }}>
            <div className="flex items-center gap-2">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Speak to SMITHERS..."
                className="flex-1 rounded-lg px-3 py-2 text-sm text-white outline-none transition-colors"
                style={{
                  fontFamily: "'Jost', sans-serif",
                  background: 'rgba(0,0,0,0.4)',
                  border: '1px solid rgba(0,240,255,0.1)',
                }}
                onFocus={(e) => { e.target.style.borderColor = 'rgba(0,240,255,0.35)'; }}
                onBlur={(e) => { e.target.style.borderColor = 'rgba(0,240,255,0.1)'; }}
                data-testid="smithers-chat-input"
              />
              <button
                onClick={handleSend}
                disabled={isThinking || !input.trim()}
                className="p-2 rounded-lg transition-all disabled:opacity-20"
                style={{
                  background: 'linear-gradient(135deg, #00F0FF, #003B6F)',
                  color: '#000',
                  boxShadow: input.trim() && !isThinking ? '0 0 12px rgba(0,240,255,0.3)' : 'none',
                }}
                data-testid="smithers-send-btn"
              >
                <Send size={14} />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Neon Sign Easter Egg — appears above orb when cord is pulled */}
      {neonSignOn && (
        <div
          className="absolute bottom-20 right-0 pointer-events-none"
          style={{
            animation: 'neonFadeIn 0.6s cubic-bezier(0.34, 1.56, 0.64, 1)',
          }}
          data-testid="orb-neon-sign"
        >
          <canvas
            ref={neonCanvasRef}
            style={{
              width: '100px',
              filter: 'brightness(1.1) drop-shadow(0 0 12px rgba(0,240,255,0.5)) drop-shadow(0 0 24px rgba(255,0,229,0.25))',
              animation: 'neonBreath 3s ease-in-out infinite',
            }}
          />
        </div>
      )}

      {/* Floating Orb Button */}
      <button
        onClick={() => setIsOpen(prev => !prev)}
        className="relative w-16 h-16 rounded-full cursor-pointer group"
        data-testid="smithers-orb-btn"
      >
        {/* Background glow layer */}
        <div
          className="absolute inset-0 rounded-full transition-all duration-500"
          style={{
            background: 'radial-gradient(circle at 40% 40%, rgba(0,240,255,0.2), rgba(0,59,111,0.4), rgba(255,0,229,0.05))',
            boxShadow: isOpen
              ? '0 0 35px rgba(0,240,255,0.5), 0 0 70px rgba(0,59,111,0.3), 0 0 10px rgba(255,0,229,0.15)'
              : '0 0 20px rgba(0,240,255,0.3), 0 0 40px rgba(0,59,111,0.2)',
          }}
        />

        {/* SVG Golden Spiral */}
        <svg
          ref={orbSvgRef}
          width={64}
          height={64}
          viewBox="0 0 64 64"
          className="absolute inset-0"
        />

        {/* Outer pulse ring */}
        <div
          className="absolute inset-[-4px] rounded-full animate-pulse-glow"
          style={{ border: '1px solid rgba(0,240,255,0.2)' }}
        />

        {/* Second outer ring (slow pulse) */}
        <div
          className="absolute inset-[-8px] rounded-full"
          style={{
            border: '1px solid rgba(0,59,111,0.15)',
            animation: 'pulse-glow 3s ease-in-out infinite 1s',
          }}
        />

        {/* Hover tooltip */}
        <div
          className="absolute -top-10 left-1/2 -translate-x-1/2 px-3 py-1 rounded-lg text-[10px] whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none font-label"
          style={{
            background: 'rgba(0,0,0,0.92)',
            color: '#00F0FF',
            border: '1px solid rgba(0,240,255,0.2)',
            letterSpacing: '0.15em',
          }}
        >
          SMITHERS AI
        </div>
      </button>

      {/* Hidden widget launcher for Smithers API integration */}
      <div id="smithers-launcher" style={{ display: 'none' }} />
    </div>
  );
}
